/*
  Landing de producto · mi-hogar
  - Aplica los colores de marca al botón de Releasit (solo en este tema).
    Releasit fija su color con estilos en línea !important, por eso se ajusta por JS.
  - Botones de la landing ([data-lp-cta]): nunca envían un formulario propio;
    pulsan el botón real de compra (Releasit o, si no existe, el de Shopify).
  - Copias del precio ([data-lp-price-mirror]) sincronizadas con el precio real de Dawn.
  - Tarjetas de oferta ([data-lp-offer]) que seleccionan la variante en el selector real.
  - Barra fija ([data-lp-sticky]) visible cuando el botón de compra no está a la vista.
  - Mueve el widget de Trustoo dentro de la sección de reseñas ([data-lp-reviews-slot]).
  No modifica product-form.js, product-info.js ni el comportamiento de Releasit o Trustoo.
*/
(() => {
  if (window.LandingPDP) return;

  const DEFAULT_COD_SELECTOR = '._rsi-buy-now-button-product';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getRoot = () => document.querySelector('product-info.lp-pdp');

  const cssVar = (name, fallback) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

  const codSelector = (root) => (root && root.dataset.lpCodSelector) || DEFAULT_COD_SELECTOR;

  const isUsable = (element) =>
    !!element &&
    element.offsetParent !== null &&
    !element.hasAttribute('disabled') &&
    element.getAttribute('aria-disabled') !== 'true';

  /* ---------- Botón de Releasit con colores de marca ---------- */
  function styleCodButtons(root) {
    if (!root) return;

    // --lp-cod-* solo existe en plantillas con landing-cod.css; las demás siguen en salvia.
    const background = cssVar('--lp-cod-bg', cssVar('--lp-salvia', '#5E7153'));
    const backgroundHover = cssVar('--lp-cod-bg-hover', cssVar('--lp-salvia-hover', '#4F6146'));
    const shadow = `0 8px 20px ${cssVar('--lp-cod-shadow', cssVar('--lp-salvia-shadow', 'rgba(94, 113, 83, 0.28)'))}`;

    root.querySelectorAll(codSelector(root)).forEach((button) => {
      button.style.setProperty('background', button.matches(':hover') ? backgroundHover : background, 'important');
      button.style.setProperty('border-radius', '999px', 'important');
      button.style.setProperty('box-shadow', shadow, 'important');

      if (button.dataset.lpStyled) return;
      button.dataset.lpStyled = 'true';
      button.addEventListener('pointerenter', () => button.style.setProperty('background', backgroundHover, 'important'));
      button.addEventListener('pointerleave', () => button.style.setProperty('background', background, 'important'));
    });
  }

  /* ---------- Botones de la landing → compra real ---------- */
  function scrollToBuybox(root) {
    // Si el bloque de compra tiene selector de ofertas, se lleva ahí.
    const buybox = root.querySelector('[data-lp-scroll-target]') || root.querySelector('[data-lp-buybox]') || root;
    buybox.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function openPurchase({ scrollOnly = false } = {}) {
    const root = getRoot();
    if (!root) return;

    scrollToBuybox(root);
    if (scrollOnly) return;

    const codButton = root.querySelector(codSelector(root));
    if (isUsable(codButton)) {
      codButton.click();
      return;
    }

    const submitButton = root.querySelector('product-form .product-form__submit');
    if (isUsable(submitButton)) submitButton.click();
  }

  /* ---------- Copias del precio ---------- */
  function syncPriceMirrors() {
    const root = getRoot();
    if (!root) return;

    const priceBox = root.querySelector('[id^="price-"]');
    const price = priceBox?.querySelector('.price');
    if (!price) return;

    const onSale = price.classList.contains('price--on-sale');
    const current = onSale
      ? price.querySelector('.price__sale .price-item--sale')
      : price.querySelector('.price__regular .price-item--regular');
    const compare = price.querySelector('.price__sale s.price-item--regular');
    const save = priceBox.querySelector('.lp-save');
    const soldOut = price.classList.contains('price--sold-out');

    document.querySelectorAll('[data-lp-price-mirror]').forEach((mirror) => {
      const currentEl = mirror.querySelector('[data-lp-price-current]');
      const compareEl = mirror.querySelector('[data-lp-price-compare]');
      const saveEl = mirror.querySelector('[data-lp-save-mirror]');

      if (currentEl && current) currentEl.textContent = current.textContent.trim();
      if (compareEl) {
        compareEl.textContent = onSale && compare ? compare.textContent.trim() : '';
        compareEl.classList.toggle('hidden', !onSale);
      }
      if (saveEl) saveEl.innerHTML = save ? save.outerHTML : '';
    });

    document.querySelectorAll('[data-lp-cta="buy"]').forEach((cta) => {
      if (soldOut) {
        cta.setAttribute('aria-disabled', 'true');
      } else {
        cta.removeAttribute('aria-disabled');
      }
    });
  }

  /* ---------- Tarjetas de oferta → selector real de variantes ---------- */
  function selectVariantByOptionValue(value) {
    const root = getRoot();
    const selects = root?.querySelector('variant-selects');
    if (!selects) return false;

    const radio = [...selects.querySelectorAll('input[type="radio"]')].find((input) => input.value === value);
    if (radio) {
      if (!radio.checked) radio.click();
      return true;
    }

    const select = selects.querySelector('select');
    const option = select && [...select.options].find((opt) => opt.value === value);
    if (option) {
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    return false;
  }

  function markSelectedOffer(variantId) {
    document.querySelectorAll('[data-lp-offer]').forEach((card) => {
      card.classList.toggle('is-selected', String(card.dataset.variantId) === String(variantId));
    });
  }

  /* ---------- Barra fija ---------- */
  let stickyUpdate;

  function initSticky() {
    const sticky = document.querySelector('[data-lp-sticky]');
    const root = getRoot();
    if (!sticky || !root) return;

    if (stickyUpdate) {
      window.removeEventListener('scroll', stickyUpdate);
      window.removeEventListener('resize', stickyUpdate);
    }

    let frame = null;
    const render = () => {
      frame = null;
      const target = root.querySelector('.product-form__buttons') || root.querySelector('[data-lp-buybox]');
      const finalSection = document.querySelector('[data-lp-final]');
      // Visible cuando el botón de compra real ya quedó arriba de la pantalla y
      // todavía no aparece el cierre (que tiene su propio botón).
      const pastBuy = target ? target.getBoundingClientRect().bottom < 0 : false;
      const finalReached = finalSection ? finalSection.getBoundingClientRect().top < window.innerHeight : false;
      const show = pastBuy && !finalReached;

      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.inert = !show;
    };

    stickyUpdate = () => {
      if (frame === null) frame = requestAnimationFrame(render);
    };

    window.addEventListener('scroll', stickyUpdate, { passive: true });
    window.addEventListener('resize', stickyUpdate, { passive: true });
    render();
  }

  /* ---------- Reseñas de Trustoo dentro de la landing ---------- */
  let reviewsObserver;

  function placeTrustooWidget() {
    const slot = document.querySelector('[data-lp-reviews-slot]');
    if (!slot) return true;

    const widget = document.getElementById('trustoo-widget-wrapper');
    if (!widget) return false;
    if (!slot.contains(widget)) slot.appendChild(widget);
    return true;
  }

  function initReviews() {
    if (placeTrustooWidget()) return;

    reviewsObserver?.disconnect();
    reviewsObserver = new MutationObserver(() => {
      if (placeTrustooWidget()) reviewsObserver.disconnect();
    });
    reviewsObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => reviewsObserver?.disconnect(), 15000);
  }

  /* ---------- Inicio ---------- */
  let codObserver;

  function init() {
    const root = getRoot();
    if (!root) return;

    styleCodButtons(root);

    // Releasit inserta su botón de forma asíncrona y puede volver a pintarlo.
    codObserver?.disconnect();
    codObserver = new MutationObserver(() => styleCodButtons(root));
    codObserver.observe(root.querySelector('product-form') || root, { childList: true, subtree: true });
    [300, 1000, 2500].forEach((delay) => setTimeout(() => styleCodButtons(root), delay));

    syncPriceMirrors();
    initSticky();
    initReviews();
  }

  document.addEventListener('click', (event) => {
    const offer = event.target.closest('[data-lp-offer]');
    if (offer) {
      if (offer.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      if (selectVariantByOptionValue(offer.dataset.optionValue)) markSelectedOffer(offer.dataset.variantId);
      const root = getRoot();
      if (root) scrollToBuybox(root);
      return;
    }

    const trigger = event.target.closest('[data-lp-cta]');
    if (!trigger) return;

    event.preventDefault();
    const scrollOnly = trigger.dataset.lpCta === 'scroll' || trigger.getAttribute('aria-disabled') === 'true';
    openPurchase({ scrollOnly });
  });

  // Dawn declara PUB_SUB_EVENTS con `const` (constants.js): no existe en `window`.
  const pubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;

  if (typeof subscribe === 'function' && pubSubEvents) {
    subscribe(pubSubEvents.variantChange, (event) => {
      styleCodButtons(getRoot());
      syncPriceMirrors();
      const variantId = event?.data?.variant?.id;
      if (variantId) markSelectedOffer(variantId);
    });
  }

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LandingPDP = { openPurchase, refresh: init };
})();
